import { Link, useParams } from "react-router-dom";
import { HeartHandshake, Radio as RadioIcon } from "lucide-react";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { useFavorites } from "@/lib/favorites";
import { Container } from "@/components/site/PageHeader";
import { SectionHeading, Label } from "@/components/site/SectionHeading";
import { TrackCard } from "@/components/site/FavoriteTracks";
import { FavoriteButton } from "@/components/site/FavoriteButton";

const btn = "inline-flex items-center gap-2 font-bold uppercase text-xs tracking-[0.2em] px-6 h-12 transition-colors";

export default function SharedFavorites() {
  const { id } = useParams();
  const { data, isLoading, isError } = useApi(`/shared-favorites/${id}`);
  const { addMany } = useFavorites();
  const tracks = Array.isArray(data?.tracks) ? data.tracks : [];

  const addAll = () => {
    const n = addMany(tracks);
    toast.success(n ? `${n} titre${n > 1 ? "s" : ""} ajouté${n > 1 ? "s" : ""} à tes favoris` : "Tu as déjà tous ces titres");
  };

  return (
    <div className="pt-36 pb-32" data-testid="shared-favorites-page">
      <Container className="grid grid-cols-1 xl:grid-cols-[1fr_1.4fr] gap-12 xl:gap-20 [&>*]:min-w-0">
        <div>
          <Label>Radio LGTDZ · Sélection partagée</Label>
          <div className="mt-6"><SectionHeading top="Une liste" bottom="de favoris" testId="shared-favorites-title" /></div>
          <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mt-8 max-w-md">
            Des titres entendus sur la radio LGTDZ et gardés par un·e auditeur·ice. Écoute-les ailleurs ou ajoute-les à tes propres favoris.
          </p>
          {tracks.length > 0 && <p className="mt-6 text-xs uppercase tracking-[0.25em] font-bold text-primary" data-testid="shared-favorites-count">{tracks.length} titre{tracks.length > 1 ? "s" : ""}</p>}
          <div className="mt-8 flex flex-wrap gap-3">
            {tracks.length > 0 && (
              <button type="button" onClick={addAll} className={`${btn} bg-primary text-white hover:bg-white hover:text-black`} data-testid="shared-favorites-add-all">
                <HeartHandshake className="w-4 h-4" /> Tout ajouter à mes favoris
              </button>
            )}
            <Link to="/radio" className={`${btn} border border-white/30 text-white hover:border-primary hover:text-primary`} data-testid="shared-favorites-radio-link">
              <RadioIcon className="w-4 h-4" /> Écouter la radio
            </Link>
          </div>
        </div>
        <div className="min-w-0">
          {isLoading && <p className="text-zinc-400" data-testid="shared-favorites-loading">Chargement…</p>}
          {isError && <p className="text-zinc-300" data-testid="shared-favorites-error">Cette liste n'existe pas ou n'est plus disponible.</p>}
          {tracks.length > 0 && (
            <ol className="border-t border-white/10" data-testid="shared-favorites-list">
              {tracks.map((t, i) => (
                <TrackCard key={`${t.artist}-${t.title}-${i}`} f={t} testId="shared-track">
                  <FavoriteButton track={t} testId="shared-track-favorite" className="w-10 h-10" />
                </TrackCard>
              ))}
            </ol>
          )}
        </div>
      </Container>
    </div>
  );
}
